/**
 * Builds the "Upcoming due items" view: combines reminder rules
 * (derived dual due date) and warranties (fixed due date) into
 * a single list ordered by urgency. Pure and testable (`now` injectable).
 */

import {
  computeDualDue,
  computeWarrantyDue,
  type DualDueResult,
  type DueStatus,
  type UsagePoint,
  WARRANTY_LEAD_DAYS,
} from "./due";

export interface UpcomingRuleSource {
  id: string;
  asset_id: string | null;
  title: string | null;
  category: string | null;
  interval_months: number | null;
  interval_usage: number | null;
  last_done_at: string | null;
  last_done_usage: number | null;
  lead_days: number | null;
  active: number | null;
  asset_name?: string | null;
  asset_kind?: string | null;
  asset_usage_unit?: string | null;
}

export interface UpcomingWarrantySource {
  id: string;
  asset_id: string | null;
  title: string | null;
  expires_at: string | null;
  asset_name?: string | null;
  asset_kind?: string | null;
}

export interface UpcomingItem {
  kind: "reminder" | "warranty";
  id: string;
  assetId: string;
  assetName: string;
  assetKind: string;
  title: string;
  category: string | null;
  status: DueStatus;
  /** Effective due date (null = unknown/usage-only with no projection). */
  dueDate: Date | null;
  daysUntilDue: number | null;
  /** Detail of the dual calculation (reminders only). */
  due: DualDueResult | null;
  usageUnit: string | null;
}

export const DEFAULT_LEAD_DAYS = 14;

const STATUS_ORDER: Record<DueStatus, number> = {
  overdue: 0,
  due_soon: 1,
  ok: 2,
  unknown: 3,
};

function compareUpcoming(a: UpcomingItem, b: UpcomingItem): number {
  const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
  if (byStatus !== 0) {
    return byStatus;
  }
  const aTime = a.dueDate?.getTime() ?? Number.POSITIVE_INFINITY;
  const bTime = b.dueDate?.getTime() ?? Number.POSITIVE_INFINITY;
  if (aTime !== bTime) {
    return aTime - bTime;
  }
  return a.title.localeCompare(b.title);
}

/**
 * Combines active rules and warranties into the upcoming-due list.
 *
 * @param readingsByAsset — reading history grouped by asset_id.
 */
export function buildUpcomingItems(options: {
  rules: UpcomingRuleSource[];
  warranties: UpcomingWarrantySource[];
  readingsByAsset: Map<string, UsagePoint[]>;
  now?: Date;
}): UpcomingItem[] {
  const now = options.now ?? new Date();
  const items: UpcomingItem[] = [];

  for (const rule of options.rules) {
    if (rule.active === 0 || !rule.asset_id) {
      continue;
    }
    const due = computeDualDue({
      intervalMonths: rule.interval_months,
      intervalUsage: rule.interval_usage,
      lastDoneAt: rule.last_done_at,
      lastDoneUsage: rule.last_done_usage,
      leadDays: rule.lead_days ?? DEFAULT_LEAD_DAYS,
      readings: options.readingsByAsset.get(rule.asset_id) ?? [],
      now,
    });
    items.push({
      kind: "reminder",
      id: rule.id,
      assetId: rule.asset_id,
      assetName: rule.asset_name ?? "",
      assetKind: rule.asset_kind ?? "other",
      title: rule.title ?? "",
      category: rule.category,
      status: due.status,
      dueDate: due.effectiveDueDate,
      daysUntilDue: due.daysUntilDue,
      due,
      usageUnit: rule.asset_usage_unit ?? null,
    });
  }

  for (const warranty of options.warranties) {
    if (!warranty.asset_id || !warranty.expires_at) {
      continue;
    }
    const due = computeWarrantyDue(warranty.expires_at, WARRANTY_LEAD_DAYS, now);
    items.push({
      kind: "warranty",
      id: warranty.id,
      assetId: warranty.asset_id,
      assetName: warranty.asset_name ?? "",
      assetKind: warranty.asset_kind ?? "other",
      title: warranty.title ?? "",
      category: null,
      status: due.status,
      dueDate: due.expiresAt,
      daysUntilDue: due.daysUntilExpiry,
      due: null,
      usageUnit: null,
    });
  }

  return items.toSorted(compareUpcoming);
}

/** Groups raw readings by asset_id (typical input from SQLite). */
export function groupReadingsByAsset(
  readings: { asset_id: string | null; value: number | null; recorded_at: string | null }[],
): Map<string, UsagePoint[]> {
  const map = new Map<string, UsagePoint[]>();
  for (const r of readings) {
    if (!r.asset_id || r.value === null || !r.recorded_at) {
      continue;
    }
    const list = map.get(r.asset_id) ?? [];
    list.push({ value: r.value, recordedAt: r.recorded_at });
    map.set(r.asset_id, list);
  }
  return map;
}
